'use client';
import React, { useState } from 'react';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { mutate } from 'swr';
import type { Entry } from 'contentful';

import { ExtendedProductFields } from '@/core/contentfulConfig';
import { addToCart } from '@/lib/cart.browser';
import { ITEM_ORIGINS, TW_PRODUCT_MERCH_CARD_CLASSES } from '@/core/constants';
import { Button, Badge, HeaderText, LuvImage } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';
import { ShopifyProductType } from '@/lib/shopify';
import { LearnMoreModal } from '@/components/common';

import { TypeHolidayBundlesCardFields } from '@/core/generated-types';
import { getBundleAvailability } from './utils';
import { CTA_COLORS } from '../types';

function HolidayBundlesCard({
  title,
  image,
  description,
  learnMoreDescription,
  learnMoreButtonText,
  bundleImages,
  price,
  maxPrice,
  originalPrice,
  includes,
  buttonText,
  includesLabel,
  badge,
  includePlayKits,
  includeBookBundles = false,
  outOfStock,
  outOfStockButtonText = 'Sold out',
}: TypeHolidayBundlesCardFields) {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const router = useRouter();

  const playKitchenProduct = includes.find(
    (item) =>
      item.fields.shopifyProduct?.tags.includes('the-play-kitchen') &&
      item.fields.shopifyProduct?.variants?.length > 1,
  ) as Entry<ExtendedProductFields> | undefined;

  const variants =
    (playKitchenProduct &&
      playKitchenProduct.fields?.shopifyProduct?.variants) ??
    [];
  const [selectedVariant, setSelectedVariant] = useState(variants?.[0]);

  const isBundleUnavailable = outOfStock
    ? outOfStock
    : getBundleAvailability(includes);

  const pricingCopy = maxPrice ? `${price} - ${maxPrice}` : `${price}`;
  const quantity = 1;

  const onModalAddToCart = async () => {
    setLoadingModal(true);
    const cartItem = {
      merchandiseId: selectedVariant.id,
      quantity: quantity,
      attributes: [
        {
          key: 'item_origin',
          value: ITEM_ORIGINS.PRODUCT_PAGE,
        },
        {
          key: 'quantity_limited_to_one',
          value: 'false',
        },
      ],
    };

    try {
      await addToCart([cartItem], quantity, { noredirect: 1 });
      if (includePlayKits) {
        setLoadingModal(false);
        router.push(
          `/pages/subscription-v3/child-info?simplifiedSubflow=true&size=${includePlayKits}&addOns=${includeBookBundles}`,
        );
      } else {
        await mutate('/cart');
        setLoadingModal(false);
        window.location.href = '/cart';
      }
    } catch (error) {
      setLoadingModal(false);
      console.error('Error adding products to cart:', error);
    }
  };

  const onAddToCart = async () => {
    setLoading(true);
    const cartItems = includes
      .filter(
        (item) =>
          item.sys.contentType.sys.id === 'product' &&
          !item.fields.isPlayKit &&
          !item.fields.shopifyProduct?.tags.includes('the-play-kitchen'),
      )
      .map((item) => {
        const isSkillSet =
          item.fields.shopifyProduct?.productType ===
          ShopifyProductType.SKILL_SET;
        const isCoursePack =
          item.fields.shopifyProduct?.productType === ShopifyProductType.COURSE;
        return {
          merchandiseId: item.fields.shopifyProduct.variants[0].id,
          quantity: quantity,
          attributes: [
            {
              key: 'item_origin',
              value: ITEM_ORIGINS.PRODUCT_PAGE,
            },
            {
              key: 'quantity_limited_to_one',
              value: String(isCoursePack || isSkillSet),
            },
            {
              key: 'is_course_pack',
              value: String(isCoursePack),
            },
            {
              key: 'is_skill_set',
              value: String(isSkillSet),
            },
          ],
        };
      });

    try {
      await addToCart(cartItems, quantity, { noredirect: 1 });
      await mutate('/cart');
      if (playKitchenProduct) {
        setLoading(false);
        setIsOpen(true);
      } else if (!playKitchenProduct && includePlayKits) {
        setLoading(false);
        router.push(
          `/pages/subscription-v3/child-info?simplifiedSubflow=true&size=${includePlayKits}&addOns=${includeBookBundles}`,
        );
      } else {
        setLoading(false);
        window.location.href = '/cart';
      }
    } catch (error) {
      setLoading(false);
      console.error('Error adding products to cart:', error);
    }
  };

  return (
    <>
      <div
        className={classNames(
          'rounded bg-white drop-shadow-md flex flex-col',
          TW_PRODUCT_MERCH_CARD_CLASSES.container,
        )}
      >
        <div className="relative">
          {badge && (
            <div className="absolute left-4 top-5 z-10">
              <Badge {...badge.fields} />
            </div>
          )}
          <figure className="rounded-t">
            <LuvImage
              height={240}
              width={360}
              imageData={image}
              alt={image?.fields?.altText || ''}
              className={classNames(
                'w-full rounded-t',
                TW_PRODUCT_MERCH_CARD_CLASSES.image,
              )}
            />
          </figure>
        </div>
        <div className="mx-6 flex flex-col grow justify-between">
          <div className="mt-6">
            <HeaderText size={HeaderSize.H3} text={title} align="left" />
            <div className="text-left mt-2 mb-3">
              <p className="m-0 inline text-base">{description}</p>
              <button
                className="inline underline font-bold ml-1"
                onClick={() => setIsOpen(true)}
              >
                {learnMoreButtonText}
              </button>
            </div>
            {includes && (
              <div
                className="flex grow flex-col"
                data-testid="product-includes-container"
              >
                <p className="text-left text-lg font-bold">{includesLabel}</p>
                {includes.map((item) => {
                  return (
                    <ul
                      className="mt-2 pl-[14px] list-disc flex items-start"
                      key={item.sys.id}
                    >
                      {item.sys.contentType.sys.id ===
                        'iconShortDescription' && (
                        <li className="text-base">{item.fields.description}</li>
                      )}
                    </ul>
                  );
                })}
              </div>
            )}
          </div>
          <div className="mt-4 mb-6">
            <div className="flex items-baseline gap-2 mb-3">
              <p className="text-h2 font-bold text-red-v2">{pricingCopy}</p>
              <p className="text-h3 font-bold text-grey-light line-through">
                {originalPrice}
              </p>
            </div>
            <Button
              ctaColor={CTA_COLORS.RED}
              onClick={onAddToCart}
              ctaText={isBundleUnavailable ? outOfStockButtonText : buttonText}
              disabled={loading || isBundleUnavailable}
              includeSpinner={loading}
              classes="disabled:bg-grey-dark disabled:border-grey-dark disabled:opacity-100"
              fullWidth
            />
          </div>
        </div>
      </div>
      <LearnMoreModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        title={title}
        description={learnMoreDescription}
        badge={badge}
        image={image}
        bundleImages={bundleImages}
        price={price}
        originalPrice={originalPrice}
        maxPrice={maxPrice}
        loading={loadingModal}
        buttonText={buttonText}
        buttonColor={CTA_COLORS.RED}
        handleClick={onModalAddToCart}
        product={playKitchenProduct}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        isBundleUnavailable={isBundleUnavailable}
        outOfStockButtonText={outOfStockButtonText}
      />
    </>
  );
}

export default HolidayBundlesCard;
